// ── Tenant Context ──
// Provides active tenant scoping throughout the app.
// Cross-tenant operators (super_admin, owner) can switch context.
// Normal users are locked to their assigned tenant.

import { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useRole } from "@/data/RoleContext";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import { ControlPlaneService } from "@/services/control-plane.service";
import type { ExternalClient } from "./types";
import type { ManagedTenant } from "@/data/controlPlane/types";
import { filterByTenant } from "./accessHelpers";
import { secureTenantQuery, requiresCrossTenantConfirmation, type TenantGuardContext, type DestructiveAction } from "./tenantGuard";
import type { TenantOwned } from "./types";

// The default tenant for the current single-tenant deployment
const DEFAULT_TENANT_ID = "tenant-001";

// Roles allowed to switch tenant context
const CROSS_TENANT_ROLES = new Set(["super_admin", "owner"]);

interface TenantContextValue {
  /** Active tenant ID (null = cross-tenant / all tenants view) */
  activeTenantId: string | null;
  /** Active tenant object */
  activeTenant: ManagedTenant | undefined;
  /** All available tenants */
  tenants: ManagedTenant[];
  /** Whether current user can switch tenants */
  canSwitchTenant: boolean;
  /** Whether in cross-tenant mode (All Companies) */
  isCrossTenantMode: boolean;
  /** Switch to a specific tenant, or null for all-tenants view */
  setActiveTenantId: (tenantId: string | null) => void;
  /** External clients scoped to active tenant */
  clients: ExternalClient[];
  /** All clients (unfiltered, for cross-tenant operators) */
  allClients: ExternalClient[];
  /** Filter helper that respects current tenant context */
  scopeByTenant: <T extends { tenantId?: string }>(records: T[]) => T[];
  /** Secure query with audit metadata */
  secureQuery: <T extends TenantOwned>(records: T[]) => { records: T[]; meta: import("./tenantGuard").QueryMeta };
  /** Check if destructive action needs confirmation */
  needsConfirmation: (action: DestructiveAction) => { required: boolean; reason: string };
  /** Guard context for passing to lower-level helpers */
  guardContext: TenantGuardContext;
  /** Switch counter — increments on every tenant change for state reset detection */
  switchGeneration: number;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { role } = useRole();
  const canSwitch = CROSS_TENANT_ROLES.has(role);

  // Cross-tenant operators start with all-tenants view; others are locked
  const [tenantId, setTenantId] = useState<string | null>(
    canSwitch ? null : DEFAULT_TENANT_ID
  );

  // Switch generation counter — drives state reset in consumers
  const [switchGeneration, setSwitchGeneration] = useState(0);
  const [tenants, setTenants] = useState<ManagedTenant[]>([]);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  useEffect(() => {
    if (deferHeavyProvider) return;

    ControlPlaneService.loadTenants().then(loaded => {
      if (loaded.length > 0) setTenants(loaded);
    });
  }, [deferHeavyProvider]);

  // Lock non-cross-tenant users to their tenant
  const effectiveTenantId = canSwitch ? tenantId : DEFAULT_TENANT_ID;
  const isCrossTenantMode = canSwitch && !effectiveTenantId;

  // Reset tenant context when role changes (prevents cross-tenant residue)
  const prevRole = useRef(role);
  useEffect(() => {
    if (prevRole.current !== role) {
      prevRole.current = role;
      const nowCanSwitch = CROSS_TENANT_ROLES.has(role);
      setTenantId(nowCanSwitch ? null : DEFAULT_TENANT_ID);
      setSwitchGeneration((g) => g + 1);
    }
  }, [role]);

  const activeTenant = useMemo(
    () => tenants.find((t) => t.id === effectiveTenantId),
    [tenants, effectiveTenantId]
  );

  const guardContext = useMemo<TenantGuardContext>(
    () => ({
      userTenantId: effectiveTenantId,
      isCrossTenant: canSwitch,
      role,
    }),
    [effectiveTenantId, canSwitch, role]
  );

  const clients = useMemo(
    () => filterByTenant(
      tenants.map((t) => ({
        id: t.id,
        tenantId: t.id,
        companyName: t.companyName,
        legalName: t.companyName,
        plan: "standard",
        primaryContactName: t.ownerName || "",
        primaryContactEmail: t.ownerEmail || "",
        phone: "",
        active: true,
        createdAt: t.createdAt || new Date().toISOString(),
      })) as ExternalClient[],
      effectiveTenantId,
    ),
    [effectiveTenantId, tenants]
  );

  const scopeByTenant = useCallback(
    <T extends { tenantId?: string }>(records: T[]) => filterByTenant(records, effectiveTenantId),
    [effectiveTenantId]
  );

  const secureQuery = useCallback(
    <T extends TenantOwned>(records: T[]) => secureTenantQuery(guardContext, records),
    [guardContext]
  );

  const needsConfirmation = useCallback(
    (action: DestructiveAction) => requiresCrossTenantConfirmation(guardContext, action),
    [guardContext]
  );

  const handleSetTenant = useCallback(
    (id: string | null) => {
      if (canSwitch) {
        setTenantId(id);
        setSwitchGeneration((g) => g + 1);
      }
    },
    [canSwitch]
  );

  return (
    <TenantContext.Provider
      value={{
        activeTenantId: effectiveTenantId,
        activeTenant,
        tenants,
        canSwitchTenant: canSwitch,
        isCrossTenantMode,
        setActiveTenantId: handleSetTenant,
        clients,
        allClients: clients,
        scopeByTenant,
        secureQuery,
        needsConfirmation,
        guardContext,
        switchGeneration,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within TenantProvider");
  return ctx;
}
